import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { geographyField } from "./geographyFormField";
import { getNextGeoId } from "../../services/geography/geography.service";


export default function CreateGeography({
    initialValues,
    onSubmit,
    onCancel,
    viewOnly,
    isEdit
}) {
    const { userDetail } = useSelector((state) => state.login);

    const [formData, setFormData] = useState({
        geoId: "",
        country: "",
        state: "",
        city: "",
        pincodeType: "",
        pincode: "",
        pincodeFrom: "",
        pincodeTo: "",
        excludedPincode: "",
        createdBy: "",
        createdDate: "",
        updatedBy: "",
        updatedDate: ""
    });

    // ================= LOAD EDIT / VIEW =================
    useEffect(() => {
        if ((viewOnly || isEdit) && initialValues) {
            let pincodeType = "";
            let pincode = "";
            let pincodeFrom = "";
            let pincodeTo = "";
            let excludedPincode = "";

            const config = initialValues.pincodeConfig;

            if (config?.type === "individual") {
                pincodeType = "Individual";
                pincode = config.codes?.join(", ");
            }

            if (config?.type === "range") {
                pincodeType = "Range";
                pincodeFrom = config.range?.from || "";
                pincodeTo = config.range?.to || "";
            }

            if (config?.type === "range_excluding") {
                pincodeType = "Range Excluding";
                pincodeFrom = config.range?.from || "";
                pincodeTo = config.range?.to || "";
                excludedPincode = config.exclude?.join(", ") || "";
            }

            setFormData({
                geoId: initialValues.geoId || "",

                country: initialValues.country || "",
                state: initialValues.state || "",
                city: initialValues.city || "",
                pincodeType,
                pincode,
                pincodeFrom,
                pincodeTo,
                excludedPincode,
                createdBy: initialValues.createdBy || "",
                createdDate: initialValues.createdDate || "",
                updatedBy: initialValues.updatedBy || "",
                updatedDate: initialValues.updatedDate || ""
            });
        }
    }, [initialValues, viewOnly, isEdit]);

    // ================= AUTO FETCH GEO ID =================
    useEffect(() => {
        if (!viewOnly && !isEdit) {
            fetchNextGeoId();
        }
    }, []);

    const fetchNextGeoId = async () => {
        try {
            const id = await getNextGeoId();

            setFormData(prev => ({
                ...prev,
                geoId: id
            }));
        } catch (err) {
            console.error("Failed to fetch Geo ID", err);
        }
    };


    // ================= AUTO FILL CREATE MODE =================
    useEffect(() => {
        if (!viewOnly && !isEdit && userDetail) {
            setFormData((prev) => ({
                ...prev,
                createdBy: `${userDetail.firstName || ""} ${userDetail.lastName || ""}`,
                createdDate: new Date().toISOString().split("T")[0]
            }));
        }
    }, [userDetail, viewOnly, isEdit]);

    const handleChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    // ================= SUBMIT =================
    const submit = (e) => {
        e.preventDefault();

        let pincodeConfig = null;

        if (formData.pincodeType === "Individual") {
            pincodeConfig = {
                type: "individual",
                codes: formData.pincode
                    ?.split(",")
                    .map((p) => Number(p.trim()))
                    .filter(Boolean)
            };
        }

        if (formData.pincodeType === "Range") {
            pincodeConfig = {
                type: "range",
                range: {
                    from: Number(formData.pincodeFrom),
                    to: Number(formData.pincodeTo)
                }
            };
        }

        if (formData.pincodeType === "Range Excluding") {
            pincodeConfig = {
                type: "range_excluding",
                range: {
                    from: Number(formData.pincodeFrom),
                    to: Number(formData.pincodeTo)
                },
                exclude: formData.excludedPincode
                    ?.split(",")
                    .map((p) => Number(p.trim()))
                    .filter(Boolean)
            };
        }

        const payload = {
            country: formData.country,
            state: formData.state,
            city: formData.city,
            pincodeConfig,
            createdBy: formData.createdBy,
            createdDate: formData.createdDate,
            updatedBy: isEdit
                ? `${userDetail.firstName || ""} ${userDetail.lastName || ""}`
                : "",
            updatedDate: isEdit
                ? new Date().toISOString().split("T")[0]
                : ""
        };

        onSubmit(payload, initialValues?._id);
    };

    return (
        <form>
            <div className="d-flex flex-wrap gap-3">
                {geographyField.map((field) => {

                    // Hide Geo ID in create
                    // if (!viewOnly && !isEdit && field.name === "geoId") return null;

                    // 🔥 Hide pincode fields until type selected
                    if (
                        !formData.pincodeType &&
                        ["pincode", "pincodeFrom", "pincodeTo", "excludedPincode"]
                            .includes(field.name)
                    ) return null;

                    // Individual
                    if (
                        formData.pincodeType === "Individual" &&
                        ["pincodeFrom", "pincodeTo", "excludedPincode"]
                            .includes(field.name)
                    ) return null;

                    // Range
                    if (
                        formData.pincodeType === "Range" &&
                        ["pincode", "excludedPincode"]
                            .includes(field.name)
                    ) return null;

                    // Range Excluding
                    if (
                        formData.pincodeType === "Range Excluding" &&
                        ["pincode"].includes(field.name)
                    ) return null;

                    return (
                        <div key={field.name} style={{ flex: "0 0 15%" }}>
                            <label className="form-label">
                                {field.label}
                                {field.required && <span className="text-danger"> *</span>}
                            </label>

                            {field.type === "select" ? (
                                <select
                                    className="form-select"
                                    value={formData[field.name] || ""}
                                    onChange={(e) =>
                                        handleChange(field.name, e.target.value)
                                    }
                                    disabled={viewOnly}
                                >
                                    <option value="">Select</option>
                                    {field.option?.map((opt) => (
                                        <option key={opt} value={opt}>
                                            {opt}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData[field.name] || ""}
                                    onChange={(e) =>
                                        handleChange(field.name, e.target.value)
                                    }
                                    readOnly={viewOnly || field.readonly}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="d-flex justify-content-center gap-2 mt-3">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onCancel}
                >
                    Back
                </button>

                {!viewOnly && (
                    <button
                        type="submit"
                        className="btn btn-primary"
                        onClick={submit}
                    >
                        {isEdit ? "Update" : "Save"}
                    </button>
                )}
            </div>
        </form>
    );
}
